import React, { useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { Alert, Container, Spinner } from 'react-bootstrap';
import { useLocation, useParams } from 'react-router-dom';
import Header from '../components/Header';
import BannerSlider from '../components/BannerSlider';
import BookingTab from '../components/BookingTab';
import TripCard from '../components/TripCard';
import CostSlider from '../components/CostSlider';
import TourTypesSlider from '../components/TourTypesSlider';
import TravelInquiryForm from '../components/TravelInquiryForm';
import MakesSpecial from '../components/MakesSpecial';
import Footer from '../components/Footer';
import WhyUsStats from '../components/WhyUsStats';
import { usePromoEnquiryMutation } from '../hooks/usePromoEnquiryMutation';
import { usePromoPageQuery } from '../hooks/usePromoPageQuery';
import { getShowOnClass, isEnabled, normalizeShowOn } from '../lib/promoFlags';
import { applyPromoCustomCode, applyPromoDesign } from '../lib/promoRuntime';

function PromoPage() {
  const { alias, previewHost } = useParams();
  const location = useLocation();
  const [quickEnquiry, setQuickEnquiry] = useState({
    name: '',
    email: '',
    mobile: '',
  });
  const [selectedEnquiryTour, setSelectedEnquiryTour] = useState('');
  const quickEnquirySubmittingRef = useRef(false);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const hostDetails = useMemo(
    () => resolvePromoHostDetails(window.location.hostname, previewHost || ''),
    [previewHost]
  );
  const subdomain = searchParams.get('subdomain') || hostDetails.companySubdomain;
  const promoSubdomain = hostDetails.promoSubdomain;
  const isPromoHubHost = hostDetails.isPromoHubHost;
  const hasPromoIdentifier = Boolean(alias || promoSubdomain);
  const [tourFilters, setTourFilters] = useState({
    destination: null,
    type: null,
    duration: null,
    guests: 1,
  });

  const promoQuery = usePromoPageQuery({ alias, subdomain, promoSubdomain });
  const quickEnquiryMutation = usePromoEnquiryMutation(alias);
  const promoData = promoQuery.data;
  const heroSlidesEnabled = isEnabled(promoData?.hero?.slides_enabled, true);
  const heroSlideTextEnabled = isEnabled(promoData?.hero?.slide_text_enabled, true);
  const enquiryEnabled = isEnabled(promoData?.enquiry?.enabled, true);
  const callbackEnabled = isEnabled(promoData?.enquiry?.callback_enabled, false);
  const contactEnabled = isEnabled(promoData?.contact?.enabled, true);
  const whyUsEnabled = isEnabled(promoData?.why_us?.enabled, false);
  const enquiryShowOn = normalizeShowOn(promoData?.enquiry?.show_on);
  const enquiryVisibilityClass = getShowOnClass(enquiryShowOn);
  const primaryTours = useMemo(() => {
    const mainTour = promoData?.tour_mapping?.main_tour ? [promoData.tour_mapping.main_tour] : [];
    const similarTours = promoData?.tour_mapping?.similar_tours || [];

    return Array.from(new Map(
      [...mainTour, ...similarTours]
        .filter(Boolean)
        .map((tour) => [tour.id ?? tour.alias ?? tour.name, tour]),
    ).values());
  }, [promoData]);
  const filteredPrimaryTours = useMemo(
    () => filterTours(primaryTours, tourFilters),
    [primaryTours, tourFilters]
  );

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const previousTitle = document.title;
    const faviconLink = ensureHeadLink("link[rel='icon']", 'icon');
    const shortcutIconLink = ensureHeadLink("link[rel='shortcut icon']", 'shortcut icon');
    const appleTouchIconLink = ensureHeadLink("link[rel='apple-touch-icon']", 'apple-touch-icon');
    const previousFaviconHref = faviconLink.getAttribute('href');
    const previousShortcutIconHref = shortcutIconLink.getAttribute('href');
    const previousAppleTouchIconHref = appleTouchIconLink.getAttribute('href');

    const websiteName = resolvePromoWebsiteName(promoData);
    document.title = websiteName;

    const faviconUrl = resolvePromoFaviconUrl(promoData);
    if (faviconUrl) {
      const faviconType = resolveFaviconType(faviconUrl);
      const faviconHref = appendCacheBuster(faviconUrl);

      faviconLink.setAttribute('href', faviconHref);
      shortcutIconLink.setAttribute('href', faviconHref);
      appleTouchIconLink.setAttribute('href', faviconHref);
      faviconLink.setAttribute('type', faviconType);
      shortcutIconLink.setAttribute('type', faviconType);
      appleTouchIconLink.setAttribute('type', faviconType);
    }

    return () => {
      document.title = previousTitle;

      if (previousFaviconHref) {
        faviconLink.setAttribute('href', previousFaviconHref);
      } else {
        faviconLink.removeAttribute('href');
      }

      if (previousShortcutIconHref) {
        shortcutIconLink.setAttribute('href', previousShortcutIconHref);
      } else {
        shortcutIconLink.removeAttribute('href');
      }

      if (previousAppleTouchIconHref) {
        appleTouchIconLink.setAttribute('href', previousAppleTouchIconHref);
      } else {
        appleTouchIconLink.removeAttribute('href');
      }
    };
  }, [promoData]);

  useEffect(() => {
    if (!promoData) {
      return undefined;
    }

    const cleanupDesign = applyPromoDesign(promoData.design || null);
    const cleanupCustomCode = applyPromoCustomCode(promoData.custom_code || null);

    return () => {
      cleanupCustomCode();
      cleanupDesign();
    };
  }, [promoData]);

  useEffect(() => {
    if (typeof window === 'undefined' || !promoData || !isPromoHubHost) {
      return;
    }

    const canonicalUrl = String(promoData?.promo?.production_url || '').trim();

    if (!canonicalUrl) {
      return;
    }

    try {
      const currentUrl = new URL(window.location.href);
      const targetUrl = new URL(canonicalUrl, currentUrl.origin);

      currentUrl.searchParams.forEach((value, key) => {
        if (!targetUrl.searchParams.has(key)) {
          targetUrl.searchParams.set(key, value);
        }
      });

      if (currentUrl.hash && !targetUrl.hash) {
        targetUrl.hash = currentUrl.hash;
      }

      if (targetUrl.toString() !== currentUrl.toString()) {
        window.location.replace(targetUrl.toString());
      }
    } catch (error) {
      // Ignore redirect parsing errors and let the page render normally.
    }
  }, [promoData, isPromoHubHost]);
  const bookingData = useMemo(
    () => ({
      ...buildBookingData(promoData),
      onSearch: ({ preset, destination, type, duration, guests }) => {
        if (preset !== 'tour') {
          return;
        }

        setTourFilters({ destination, type, duration, guests });
      },
    }),
    [promoData]
  );

  if (promoQuery.isLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!hasPromoIdentifier || !subdomain || promoQuery.isError || !promoQuery.data) {
    return (
      <div className="min-vh-100 bg-light">
        <Header />
        <Container className="py-5">
          <Alert variant="danger">
            {!hasPromoIdentifier || !subdomain
              ? 'Missing promo alias or promo subdomain mapping.'
              : promoQuery.error?.message || 'Promo not found.'}
          </Alert>
        </Container>
      </div>
    );
  }

  const otherTours = promoData?.other_tours?.items || [];
  const tourCategories = promoData?.tour_categories?.items || [];
  const handleSelectEnquiryTour = (tourName) => {
    if (!enquiryEnabled) {
      return;
    }

    const normalizedTourName = String(tourName || '').trim();

    if (!normalizedTourName) {
      return;
    }

    setSelectedEnquiryTour(normalizedTourName);
    window.location.hash = 'inquire-now';

    const enquirySection = document.getElementById('inquire-now');
    if (enquirySection) {
      enquirySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div id="home">
      <Header
        promoData={promoData}
        callbackEnabled={callbackEnabled}
        enquiryEnabled={enquiryEnabled}
        contactEnabled={contactEnabled}
      />
      {heroSlidesEnabled ? (
        <BannerSlider hero={promoData.hero} slideTextEnabled={heroSlideTextEnabled} />
      ) : null}
      <BookingTab bookingData={bookingData} hasHero={heroSlidesEnabled} />

      <div id="tours" className="bg-img">
        <TripCard
          tours={filteredPrimaryTours}
          contact={promoData?.contact || {}}
          contactEnabled={contactEnabled}
          imageBaseUrl={promoData?.design?.imgurl || ''}
          fallbackLogoUrl={promoData?.promo?.logo_url || ''}
          heading={promoData?.tour_mapping?.heading || 'Most Favorite Tour Place'}
          description={promoData?.tour_mapping?.description || ''}
          onSelectTour={handleSelectEnquiryTour}
        />
      </div>

      {whyUsEnabled ? <WhyUsStats whyUs={promoData?.why_us || null} /> : null}

      {otherTours.length ? (
        <div className="sliderBg">
          <CostSlider tours={otherTours} />
        </div>
      ) : null}

      <div id="similar-tours" className="bg-tours pt-50 pb-100">
        <section className="heading-section mb-30 plr">
          <Container className="text-center heading-container">
            <h2 className="fw-bolder">{promoData?.other_tours?.heading || 'Tour Categories'}</h2>
            <p className="fw-semibold darkgray-text mb-0 title-paragraph">
              {promoData?.other_tours?.description || promoData?.tour_mapping?.description || ''}
            </p>
          </Container>
        </section>

        <TourTypesSlider
          items={tourCategories}
          tours={promoData?.tour_mapping?.all_tours || []}
          onSelectTour={handleSelectEnquiryTour}
        />
      </div>

      {enquiryEnabled ? (
        <div id="inquire-now" className={enquiryVisibilityClass}>
          <TravelInquiryForm
            promoData={promoData}
            alias={alias}
            promoSubdomain={promoSubdomain}
            subdomain={subdomain}
          selectedTour={selectedEnquiryTour}
          callbackEnabled={callbackEnabled}
          contactEnabled={contactEnabled}
        />
      </div>
      ) : null}

      {enquiryEnabled ? (
        <section className={`holiday-section d-flex align-items-center justify-content-center mt-100 ${enquiryVisibilityClass}`.trim()}>
        <Container>
          <div className="text-center text-white">
            <h3 className="fw-bold">
              {promoData?.enquiry?.title || 'Customize your holiday package with'}
              <br />
              our travel experts
            </h3>
            <p className="fw-semibold mb-0 title-paragraph">
              {promoData?.enquiry?.description || promoData?.hero?.description || ''}
            </p>
          </div>

          <div className="form-wrapper d-flex justify-content-center align-items-center">
            <form
              className="d-flex formContainer mt-50"
              onSubmit={async (event) => {
                event.preventDefault();
                if (quickEnquirySubmittingRef.current || quickEnquiryMutation.isPending) {
                  return;
                }

                quickEnquirySubmittingRef.current = true;

                if (!subdomain || !hasPromoIdentifier) {
                  await Swal.fire({
                    icon: 'error',
                    title: 'Unable to submit',
                    text: 'Missing promo mapping details for this enquiry.',
                    confirmButtonColor: '#17bfc6',
                  });
                  quickEnquirySubmittingRef.current = false;
                  return;
                }

                try {
                  await quickEnquiryMutation.mutateAsync({
                    name: quickEnquiry.name.trim(),
                    email: quickEnquiry.email.trim(),
                    mobile: quickEnquiry.mobile.trim(),
                    alias,
                    subdomain,
                    promo_subdomain: promoSubdomain,
                    page_url: window.location.href,
                  });

                  setQuickEnquiry({
                    name: '',
                    email: '',
                    mobile: '',
                  });
                  quickEnquiryMutation.reset();
                  await Swal.fire({
                    icon: 'success',
                    title: 'Enquiry submitted',
                    text: 'Your enquiry has been submitted successfully.',
                    confirmButtonColor: '#17bfc6',
                  });
                } catch (error) {
                  await Swal.fire({
                    icon: 'error',
                    title: 'Submission failed',
                    text: error?.message || 'Unable to submit enquiry.',
                    confirmButtonColor: '#17bfc6',
                  });
                } finally {
                  quickEnquirySubmittingRef.current = false;
                }
              }}
            >
              <input
                type="text"
                className="form-control fw-semibold"
                placeholder="Enter your Name*"
                aria-label="Enter your Name"
                value={quickEnquiry.name}
                onChange={(event) => {
                  quickEnquiryMutation.reset();
                  setQuickEnquiry((current) => ({ ...current, name: event.target.value }));
                }}
                required
              />
              <input
                type="email"
                className="form-control fw-semibold"
                placeholder="Enter your Email"
                aria-label="Enter your Email"
                value={quickEnquiry.email}
                onChange={(event) => {
                  quickEnquiryMutation.reset();
                  setQuickEnquiry((current) => ({ ...current, email: event.target.value }));
                }}
                required
              />
              <input
                type="tel"
                className="form-control fw-semibold"
                placeholder="Enter your Mobile No*"
                aria-label="Enter your Mobile No"
                value={quickEnquiry.mobile}
                onChange={(event) => {
                  quickEnquiryMutation.reset();
                  setQuickEnquiry((current) => ({ ...current, mobile: event.target.value }));
                }}
                required
              />
              <button type="submit" className="btn submit-btn text-white fw-bold" disabled={quickEnquiryMutation.isPending}>
                {promoData?.enquiry?.submit_label || 'Submit'}
              </button>
            </form>
          </div>
        </Container>
        </section>
      ) : null}

      <section className="heading-section mt-100 mb-30 plr">
        <Container className="text-center heading-container">
          <h2 className="fw-bolder black-heading">
            {promoData?.special_section?.title || 'What Makes Us Special'}
          </h2>
          <p className="fw-semibold darkgray-text mb-0 title-paragraph">
            {promoData?.special_section?.description || ''}
          </p>
 
          <MakesSpecial items={promoData?.special_section?.items || []} />
        </Container>
      </section>

      <div id="contacts">
        <Footer promoData={promoData} callbackEnabled={callbackEnabled} contactEnabled={contactEnabled} />
      </div>
    </div>
  );
}

function buildBookingData(promoData) {
  const tours = promoData?.tour_mapping?.all_tours || [];
  const destinations = Array.from(new Map(
    tours
      .map((tour) => {
        const title = tour.location || tour.start_city || tour.end_city || '';
        return title ? [title, { id: title.toLowerCase().replace(/\s+/g, '-'), title }] : null;
      })
      .filter(Boolean),
  ).values());

  const durations = Array.from(new Map(
    tours
      .map((tour) => tour.duration ? [tour.duration, { id: tour.duration.toLowerCase().replace(/\s+/g, '-'), title: tour.duration }] : null)
      .filter(Boolean),
  ).values());

  const tourTypes = Array.from(new Map(
    (promoData?.tour_types?.items || [])
      .map((item) => {
        const title = String(item.name || '').trim();
        const key = `${item.id ?? title}-${title.toLowerCase()}`;
        return title ? [key, { id: item.id ?? key, title }] : null;
      })
      .filter(Boolean),
  ).values());

  return {
    destinations,
    durations,
    tourTypes,
  };
}

function filterTours(tours, filters) {
  return (tours || []).filter((tour) => {
    if (filters.destination) {
      const destinationTitle = String(filters.destination.title || '').toLowerCase();
      const location = String(tour.location || tour.start_city || tour.end_city || '').toLowerCase();

      if (destinationTitle && location !== destinationTitle) {
        return false;
      }
    }

    if (filters.duration) {
      const selectedDuration = String(filters.duration.title || '').toLowerCase();
      const tourDuration = String(tour.duration || '').toLowerCase();

      if (selectedDuration && tourDuration !== selectedDuration) {
        return false;
      }
    }

    if (filters.guests) {
      const requestedGuests = Number(filters.guests) || 1;
      const supportedGuests = getTourGuestCapacity(tour);

      if (supportedGuests !== null && supportedGuests < requestedGuests) {
        return false;
      }
    }

    if (filters.type) {
      const selectedType = String(filters.type.title || '').toLowerCase();
      const tourTypeMatches = Array.isArray(tour.tour_types) && tour.tour_types.some((item) => {
        const name = String(item.name || '').toLowerCase();
        const alias = String(item.alias || '').toLowerCase();
        return selectedType === name || selectedType === alias;
      });

      if (selectedType && !tourTypeMatches) {
        return false;
      }
    }

    return true;
  });
}

function getTourGuestCapacity(tour) {
  const candidates = [
    tour?.persons,
    tour?.person,
    tour?.people,
    tour?.guests,
    tour?.guest,
    tour?.max_persons,
    tour?.max_people,
    tour?.max_guests,
    tour?.capacity,
    tour?.pax,
  ];

  for (const value of candidates) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const match = value.match(/\d+/);

      if (match) {
        return Number(match[0]);
      }
    }
  }

  return 2;
}

function resolvePromoHostDetails(hostname, fallbackHost = '') {
  const sourceHost = (hostname || fallbackHost || '').trim().toLowerCase();

  if (!sourceHost || sourceHost === 'localhost' || sourceHost === '127.0.0.1') {
    return {
      companySubdomain: '',
      promoSubdomain: '',
      isPromoHubHost: false,
    };
  }

  const parts = sourceHost.split('.').filter(Boolean);

  if (parts[parts.length - 1] === 'localhost') {
    const localParts = parts.slice(0, -1);

    if (localParts.length <= 1) {
      return {
        companySubdomain: localParts[0] || '',
        promoSubdomain: '',
        isPromoHubHost: false,
      };
    }

    return {
      companySubdomain: localParts.slice(1).join('.'),
      promoSubdomain: localParts[0] || '',
      isPromoHubHost: localParts[0] === 'promo',
    };
  }

  if (parts.length <= 2) {
    return {
      companySubdomain: sourceHost,
      promoSubdomain: '',
      isPromoHubHost: parts[0] === 'promo',
    };
  }

  return {
    companySubdomain: parts.slice(1).join('.'),
    promoSubdomain: parts[0] || '',
    isPromoHubHost: parts[0] === 'promo',
  };
}

function resolvePromoWebsiteName(promoData) {
  const candidates = [
    promoData?.promo?.company?.name,
    promoData?.promo?.name,
    promoData?.promo?.meta_title,
  ];

  for (const value of candidates) {
    const normalizedValue = String(value || '').trim();
    if (normalizedValue) {
      return normalizedValue;
    }
  }

  return 'TripRex';
}

function resolvePromoFaviconUrl(promoData) {
  const candidates = [promoData?.promo?.logo_url, promoData?.promo?.company?.logo_url];

  for (const value of candidates) {
    const normalizedUrl = normalizePromoAssetUrl(value);
    if (normalizedUrl) {
      return normalizedUrl;
    }
  }

  return '';
}

function normalizePromoAssetUrl(value) {
  const source = String(value || '').trim();

  if (!source || typeof window === 'undefined') {
    return '';
  }

  try {
    return new URL(source, window.location.origin).toString();
  } catch (error) {
    return source;
  }
}

function resolveFaviconType(value) {
  const pathname = String(value || '').split('?')[0].toLowerCase();

  if (pathname.endsWith('.png')) {
    return 'image/png';
  }

  if (pathname.endsWith('.svg')) {
    return 'image/svg+xml';
  }

  if (pathname.endsWith('.jpg') || pathname.endsWith('.jpeg')) {
    return 'image/jpeg';
  }

  if (pathname.endsWith('.webp')) {
    return 'image/webp';
  }

  if (pathname.endsWith('.ico')) {
    return 'image/x-icon';
  }

  return 'image/png';
}

function appendCacheBuster(value) {
  try {
    const url = new URL(value, window.location.origin);
    url.searchParams.set('v', Date.now().toString());
    return url.toString();
  } catch (error) {
    return value;
  }
}

function ensureHeadLink(selector, rel) {
  let link = document.querySelector(selector);

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }

  return link;
}

export default PromoPage;

